import { useCallback, useEffect, useRef } from 'react';

export type VisibilityChange = {
  id: string;
  isVisible: boolean;
};

type UseVisibilityObserverParams = {
  onChange: (changes: VisibilityChange[]) => void;
  rootMargin?: string;
};

type ObserveRef = (node: Element | null) => void;

type UseVisibilityObserverReturn = {
  observe: (id: string) => ObserveRef;
};

const DEFAULT_ROOT_MARGIN = '400px';

const toVisibilityChanges = (
  entries: IntersectionObserverEntry[],
  idByNode: Map<Element, string>
): VisibilityChange[] =>
  entries.flatMap((entry: IntersectionObserverEntry) => {
    const id = idByNode.get(entry.target);

    return id ? [{ id, isVisible: entry.isIntersecting }] : [];
  });

export function useVisibilityObserver({
  onChange,
  rootMargin = DEFAULT_ROOT_MARGIN,
}: UseVisibilityObserverParams): UseVisibilityObserverReturn {
  const onChangeRef = useRef(onChange);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const idByNodeRef = useRef(new Map<Element, string>());
  const nodeByIdRef = useRef(new Map<string, Element>());
  const refByIdRef = useRef(new Map<string, ObserveRef>());

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const idByNode = idByNodeRef.current;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const changes = toVisibilityChanges(entries, idByNode);

        if (changes.length > 0) {
          onChangeRef.current(changes);
        }
      },
      { rootMargin }
    );

    observerRef.current = observer;
    idByNode.forEach((_id: string, node: Element) => observer.observe(node));

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [rootMargin]);

  const observe = useCallback((id: string): ObserveRef => {
    const existingRef = refByIdRef.current.get(id);
    if (existingRef) return existingRef;

    const observeRef: ObserveRef = (node) => {
      const previousNode = nodeByIdRef.current.get(id);

      if (previousNode) {
        observerRef.current?.unobserve(previousNode);
        idByNodeRef.current.delete(previousNode);
        nodeByIdRef.current.delete(id);
      }

      if (!node) {
        onChangeRef.current([{ id, isVisible: false }]);
        return;
      }

      idByNodeRef.current.set(node, id);
      nodeByIdRef.current.set(id, node);
      observerRef.current?.observe(node);
    };

    refByIdRef.current.set(id, observeRef);

    return observeRef;
  }, []);

  return { observe };
}
